import React, { useMemo, useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  IconButton,
  Tooltip,
  Alert,
  ListSubheader,
  TextField,
  InputAdornment,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useFormContext } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { startCase } from 'lodash';
import Button from '~/src/components/Button';
import { Close, HelpOutline, Check, Search } from '@mui/icons-material';
import {
  type FieldOption,
  type FieldMappingResponse,
  useFieldMappingSave,
  keys as fieldMappingKeys,
} from '~/src/entities/fieldMapping';
import { unsanitizeProjectRecord } from '~/src/entities/project';
import { useUpdateProject } from '~/src/entities/project/hooks';
import { keys as projectKeys } from '~/src/entities/project/keys';
import type { FieldMappingOverrideSubject } from '~/src/entities/fieldMapping/hooks/useFieldMappingOverride';
import { getFieldMappingOverrideSubjectType } from '~/src/entities/fieldMapping/services/fieldMappingService';
import { useFieldMappingContext } from '~/src/pages/Populate/FieldMappingProvider';
import { useProjectContext } from '~/src/pages/Populate/ProjectProvider';
import { FieldType, StringFieldType } from '~/src/entities/project/types';
import TruncatedText from './TruncatedText';

type FieldMappingWidgetProps = {
  onClose: () => void;
  onMappingCreated?: (response?: FieldMappingResponse) => void;
  initialValue?: string;
  fieldId: string;
  isRoleCard: boolean;
  fieldMappingId?: number;
  contactId?: number;
};

const FieldMappingWidget = ({
  onClose,
  onMappingCreated,
  initialValue = '',
  fieldId,
  isRoleCard,
  fieldMappingId,
  contactId,
}: FieldMappingWidgetProps) => {
  const project = useProjectContext();
  const {
    options: allOptions,
    getFilteredOptions,
    loading: fieldMappingOptionsLoading,
    error: fieldMappingOptionsError,
  } = useFieldMappingContext();
  const { getValues, setValue } = useFormContext();
  const queryClient = useQueryClient();
  const { mutateAsync: updateProject } = useUpdateProject({
    onSuccess: () => {
      queryClient.invalidateQueries(projectKeys.detail(project.id));
      queryClient.invalidateQueries(fieldMappingKeys.sourceFields(project.id));
    },
  });

  const [selectedClioField, setSelectedClioField] = useState(
    initialValue || '',
  );
  const [searchQuery, setSearchQuery] = useState('');

  const field = useMemo(() => {
    return project.stack_data.fields.find((f) => f.fid === fieldId);
  }, [project.stack_data.fields, fieldId]);

  const filteredOptions = useMemo(() => {
    if (!field) return [];
    return getFilteredOptions(field.variable_type, isRoleCard, contactId);
  }, [getFilteredOptions, field, isRoleCard, contactId]);

  const getValueFromSelectedOption = useCallback(
    (selectedPath: string): string | null => {
      if (!selectedPath) return null;
      const attribute = selectedPath.split('.').at(-1) ?? '';
      const option = filteredOptions.find(
        (o) => o.normalized_attribute === attribute,
      );
      return option?.value ?? null;
    },
    [filteredOptions],
  );

  const handleSuccess = useCallback(
    async (response?: FieldMappingResponse) => {
      const clioValue = getValueFromSelectedOption(selectedClioField);
      const allValues = getValues();
      const updatedValues = { ...allValues, [fieldId]: clioValue ?? '' };
      setValue(fieldId, clioValue ?? '');

      try {
        await updateProject({
          projectId: project.id,
          stack_saved_data: unsanitizeProjectRecord(updatedValues),
        });
        onMappingCreated?.(response);
      } catch (error) {
        console.error(
          'Error updating project after saving field mapping change:',
          error,
        );
      }

      onClose();
    },
    [
      selectedClioField,
      getValueFromSelectedOption,
      fieldId,
      getValues,
      setValue,
      updateProject,
      project.id,
      onMappingCreated,
      onClose,
    ],
  );

  const isSingleOrMultiSelectField = useMemo(() => {
    if (!field) return false;
    return [
      FieldType.MULTIPLE_CHOICE,
      StringFieldType.MULTIPLE_CHOICE,
      FieldType.BATCH_SELECT,
      StringFieldType.BATCH_SELECT,
    ].includes(field.variable_type as any);
  }, [field]);

  const fieldMappingSubjectType =
    getFieldMappingOverrideSubjectType(isRoleCard);
  const fieldMappingSubjectId =
    fieldMappingSubjectType === 'matter'
      ? project.stack_data.project_metadata?.matter_id
      : contactId;
  const fieldMappingOverrideSubject: FieldMappingOverrideSubject | undefined =
    fieldMappingSubjectId != null
      ? { fieldMappingSubjectType, fieldMappingSubjectId }
      : undefined;
  const shouldSetOverrideOnDeleteOrUpdate =
    isSingleOrMultiSelectField && !!fieldMappingOverrideSubject;

  const { isSaving, saveError, handleSaveChanges } = useFieldMappingSave({
    projectId: project.id,
    fieldId,
    fieldMappingId,
    initialValue,
    selectedValue: selectedClioField,
    overrideConfig: shouldSetOverrideOnDeleteOrUpdate
      ? {
          enabled: true,
          subjectType: fieldMappingOverrideSubject!.fieldMappingSubjectType,
          subjectId: fieldMappingOverrideSubject!.fieldMappingSubjectId,
        }
      : { enabled: false, subjectType: 'matter', subjectId: 0 },
    onSuccess: handleSuccess,
  });

  const hasChanges = selectedClioField !== initialValue;

  const handleClioFieldChange = (event: SelectChangeEvent<string>) => {
    setSelectedClioField(event.target.value as string);
  };

  const handleClear = () => {
    setSelectedClioField('');
    setSearchQuery('');
  };

  const handleCancel = () => {
    onClose();
  };

  const isSaveEnabled =
    (hasChanges || !!selectedClioField) &&
    !fieldMappingOptionsLoading &&
    !isSaving;

  const isDropdownDisabled =
    fieldMappingOptionsLoading ||
    (filteredOptions.length === 0 && !selectedClioField);

  const entityName = useMemo(() => {
    if (isRoleCard && contactId) {
      const contactGroup = allOptions.contact_fields[String(contactId)];
      return contactGroup?.role ?? 'contact';
    }
    return 'clio_matter';
  }, [isRoleCard, contactId, allOptions.contact_fields]);

  const getFullFieldPath = (option: FieldOption) =>
    `${entityName}.${option.normalized_attribute}`;

  const getAttributeFromPath = (path: string): string => {
    return path.split('.').at(-1) ?? '';
  };

  // Get formatted attribute title, falling back to startCase of normalized_attribute
  const getAttributeTitle = (option: FieldOption): string => {
    if (
      option.attribute_text &&
      option.attribute_text !== option.normalized_attribute
    ) {
      return option.attribute_text;
    }
    return startCase(option.normalized_attribute);
  };

  // Options shown in the dropdown after applying the search filter. We match on the
  // displayed title so search matches exactly what the user sees. The full
  // `filteredOptions` list is still used for value/display lookups so a selected
  // value renders correctly even when filtered out of the visible list.
  const visibleOptions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return filteredOptions;
    const selectedAttribute = getAttributeFromPath(selectedClioField);
    return filteredOptions.filter(
      (option) =>
        getAttributeTitle(option).toLowerCase().includes(query) ||
        // Always keep the selected option so the Select's value stays in range.
        option.normalized_attribute === selectedAttribute,
    );
    // getAttributeTitle / getAttributeFromPath are pure helpers over their args.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredOptions, searchQuery, selectedClioField]);

  const findOptionByAttribute = (
    options: FieldOption[],
    value: string,
  ): FieldOption | undefined => {
    const attributeFromValue = getAttributeFromPath(value);
    return options.find((o) => o.normalized_attribute === attributeFromValue);
  };

  // Get display label with value for the selected dropdown display (e.g., "Description: some value")
  const getDisplayLabelWithValue = (value: string): string => {
    const option = findOptionByAttribute(filteredOptions, value);
    if (option) {
      const hasValue = option.value != null && option.value !== '';
      const displayValue = hasValue ? option.value : '-';
      return `${getAttributeTitle(option)}: ${displayValue}`;
    }
    // Fallback: parse and format the attribute portion
    const attribute = getAttributeFromPath(value);
    return startCase(attribute);
  };

  // We need to generalize the attribute matching to consider when a contact is assigned to a card
  // But the match wasn't originally designed for their entity - i.e. the db has lawyer1.name
  // But the current assigned contact has lawyer2.name
  const isOptionSelected = (
    option: FieldOption,
    currentValue: string,
  ): boolean => {
    if (!currentValue) return false;
    const currentAttribute = getAttributeFromPath(currentValue);
    return option.normalized_attribute === currentAttribute;
  };

  const renderOptions = (options: FieldOption[], currentValue?: string) => {
    return options.map((option) => {
      const fullPath = getFullFieldPath(option);
      const isSelected = isOptionSelected(option, currentValue || '');
      const hasValue = option.value != null && option.value !== '';

      return (
        <MenuItem
          key={fullPath}
          value={fullPath}
          sx={{
            padding: '12px 20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            '&.Mui-selected': {
              backgroundColor: 'rgba(25, 118, 210, 0.08)',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minWidth: 0,
            }}
          >
            <TruncatedText
              text={getAttributeTitle(option)}
              sx={{
                fontSize: '16px',
                lineHeight: '24px',
                color: 'text.primary',
              }}
            />
            <TruncatedText
              text={hasValue ? option.value! : '-'}
              sx={{
                fontSize: '13px',
                lineHeight: '18px',
                color: 'text.secondary',
                mt: 0.25,
              }}
            />
          </Box>
          {isSelected && (
            <Check
              sx={{ fontSize: 18, color: 'text.primary', ml: 1, mt: 0.5 }}
            />
          )}
        </MenuItem>
      );
    });
  };

  return (
    <Card
      raised
      sx={{
        position: 'absolute',
        top: '0',
        transform: 'translateY(-35%)',
        left: 'calc(100% + 45px)',
        width: '300px',
        minWidth: '300px',
        zIndex: 1000,
        overflow: 'visible',
        borderRadius: '8px',
        boxShadow: '0 7px 24px 0 rgba(28,34,45,0.24)',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: '-15px',
          top: '65px',
          width: 0,
          height: 0,
          borderTop: '15px solid transparent',
          borderBottom: '15px solid transparent',
          borderRight: '15px solid white',
        },
      }}
    >
      <CardContent sx={{ padding: '20px' }}>
        <Box
          sx={{ display: 'flex', marginBottom: '16px', alignItems: 'center' }}
        >
          <Box
            sx={{ display: 'flex', alignItems: 'center', marginRight: 'auto' }}
          >
            <Typography variant="subheading" sx={{ fontWeight: 600 }}>
              Use Clio Manage data
            </Typography>
            <Tooltip
              title="Add or change which Clio Manage field to fill data from."
              slotProps={{
                tooltip: {
                  sx: { maxWidth: 148 },
                },
              }}
            >
              <IconButton
                aria-label="Clio Manage data help"
                size="small"
                sx={{
                  marginLeft: '4px',
                  padding: '2px',
                  color: 'grey.500',
                }}
              >
                <HelpOutline sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
          <Box>
            <IconButton
              sx={{ width: 16, height: 16, padding: 0 }}
              aria-label="close"
              onClick={onClose}
            >
              <Close sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ marginBottom: '8px' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '8px',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: '14px',
                fontWeight: 500,
                color: 'text.primary',
              }}
            >
              Clio Manage field
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: '14px',
                color: 'primary.main',
                cursor: 'pointer',
                fontWeight: 500,
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
              onClick={handleClear}
            >
              Clear
            </Typography>
          </Box>
          <FormControl
            fullWidth
            size="small"
            error={!!fieldMappingOptionsError}
          >
            <Select
              value={selectedClioField}
              onChange={handleClioFieldChange}
              onClose={() => setSearchQuery('')}
              displayEmpty
              disabled={isDropdownDisabled}
              renderValue={(value) => {
                if (!value) {
                  return (
                    <Typography sx={{ color: 'text.disabled' }}>
                      {isDropdownDisabled
                        ? 'No Clio Manage fields available'
                        : 'Select a field...'}
                    </Typography>
                  );
                }
                return (
                  <TruncatedText
                    text={getDisplayLabelWithValue(value)}
                    sx={{ fontSize: '14px' }}
                  />
                );
              }}
              sx={{
                fontSize: '14px',
                '& .MuiSelect-select': {
                  padding: '8px 12px',
                },
              }}
              MenuProps={{
                autoFocus: false,
                PaperProps: {
                  sx: {
                    mt: 1,
                    borderRadius: 2,
                    boxShadow: '0 7px 24px 0 rgba(28,34,45,0.24)',
                    overflowY: 'auto',
                    maxHeight: 420,
                    width: 260,
                  },
                },
              }}
            >
              <ListSubheader
                sx={{
                  p: 1,
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  bgcolor: 'background.paper',
                }}
              >
                <TextField
                  size="small"
                  fullWidth
                  autoFocus
                  placeholder="Search fields..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  // Keep the Select's built-in type-ahead from stealing keystrokes,
                  // but let Escape bubble so the menu can still close.
                  onKeyDown={(e) => {
                    if (e.key !== 'Escape') {
                      e.stopPropagation();
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                    sx: { fontSize: '14px' },
                  }}
                />
              </ListSubheader>
              {visibleOptions.length === 0
                ? [
                    <MenuItem key="__no_matches__" disabled>
                      No matching fields
                    </MenuItem>,
                  ]
                : renderOptions(visibleOptions, selectedClioField)}
            </Select>
            {fieldMappingOptionsError && (
              <FormHelperText sx={{ fontSize: '12px', mx: 0 }}>
                {fieldMappingOptionsError}
              </FormHelperText>
            )}
          </FormControl>
        </Box>

        {saveError && !isSaving && (
          <Box sx={{ marginBottom: '16px' }}>
            <Alert severity="error" sx={{ fontSize: '14px' }}>
              {saveError}
            </Alert>
          </Box>
        )}

        {hasChanges && selectedClioField && (
          <>
            <Typography
              variant="caption"
              sx={{
                fontSize: '12px',
                color: 'text.secondary',
                display: 'block',
                marginBottom: '16px',
                lineHeight: '16px',
              }}
            >
              This Clio Manage field will be used to auto-fill this document
              field in all other templates.
            </Typography>
          </>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
          <Button style={{ marginRight: 10 }} onClick={handleCancel}>
            Cancel
          </Button>
          <Button primary disabled={!isSaveEnabled} onClick={handleSaveChanges}>
            {isSaving ? 'Saving...' : 'Save changes'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default FieldMappingWidget;
